import {
  CORPORATE_HERO_DEFAULT_TEMPLATE,
  CORPORATE_HERO_TEMPLATE_CATALOG,
  defaultCorporateHeroSectionSettings,
  type CorporateHeroDesignSettings,
  type CorporateHeroLayoutSettings,
  type CorporateHeroSectionSettings,
  type CorporateHeroSettingsJson,
  type CorporateHeroTemplateType,
} from '../types/corporateHero';

const VALID_TEMPLATE_TYPES = CORPORATE_HERO_TEMPLATE_CATALOG.map((item) => item.id);

function readField(data: Record<string, unknown>, camel: string): unknown {
  if (camel in data) return data[camel];
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  return data[pascal];
}

function readString(data: Record<string, unknown>, camel: string, fallback = ''): string {
  const value = readField(data, camel);
  return value != null ? String(value) : fallback;
}

function readNumber(data: Record<string, unknown>, camel: string, fallback: number): number {
  const value = readField(data, camel);
  if (value == null || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function coerceBool(value: unknown, fallback: boolean): boolean {
  if (value == null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return fallback;
}

function resolveEnabled(
  data: Record<string, unknown>,
  parsedJson: CorporateHeroSettingsJson
): boolean {
  const topLevel = readField(data, 'heroEnabled') ?? readField(data, 'enabled');
  if (topLevel != null && topLevel !== '') {
    return coerceBool(topLevel, defaultCorporateHeroSectionSettings.enabled);
  }
  if (typeof parsedJson.enabled === 'boolean') {
    return parsedJson.enabled;
  }
  return defaultCorporateHeroSectionSettings.enabled;
}

function parseSettingsJson(raw: unknown): CorporateHeroSettingsJson {
  if (!raw) {
    return {};
  }

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as CorporateHeroSettingsJson;
    } catch {
      return {};
    }
  }

  if (typeof raw === 'object') {
    return raw as CorporateHeroSettingsJson;
  }

  return {};
}

function resolveTemplateType(
  data: Record<string, unknown>,
  jsonTemplateType?: CorporateHeroTemplateType
): CorporateHeroTemplateType {
  const candidates = [
    data.heroTemplateType,
    data.HeroTemplateType,
    data.templateType,
    data.TemplateType,
    jsonTemplateType,
  ];

  for (const candidate of candidates) {
    if (VALID_TEMPLATE_TYPES.includes(candidate as CorporateHeroTemplateType)) {
      return candidate as CorporateHeroTemplateType;
    }
  }

  return CORPORATE_HERO_DEFAULT_TEMPLATE;
}

function mergeDesign(jsonDesign?: CorporateHeroDesignSettings): CorporateHeroDesignSettings {
  return {
    ...defaultCorporateHeroSectionSettings.design,
    ...(jsonDesign ?? {}),
  };
}

function mergeLayout(jsonLayout?: CorporateHeroLayoutSettings): CorporateHeroLayoutSettings {
  const base = defaultCorporateHeroSectionSettings.layout;
  const incoming = jsonLayout ?? {};
  return {
    ...base,
    ...incoming,
    stats: Array.isArray(incoming.stats) ? incoming.stats : base.stats,
    primaryButtons: Array.isArray(incoming.primaryButtons)
      ? incoming.primaryButtons
      : base.primaryButtons,
    secondaryButtons: Array.isArray(incoming.secondaryButtons)
      ? incoming.secondaryButtons
      : base.secondaryButtons,
    slides: Array.isArray(incoming.slides) ? incoming.slides : base.slides,
  };
}

export function normalizeCorporateHeroSection(
  raw: unknown,
  overrides?: Partial<CorporateHeroSectionSettings>
): CorporateHeroSectionSettings {
  const data =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : ({} as Record<string, unknown>);

  const settingsJsonRaw =
    readField(data, 'heroSettingsJson') ?? readField(data, 'settingsJson') ?? data.settingsJson;

  const parsedJson = parseSettingsJson(settingsJsonRaw);
  const templateType = resolveTemplateType(data, parsedJson.templateType);

  const heroSection: CorporateHeroSectionSettings = {
    title: readString(data, 'heroTitle', defaultCorporateHeroSectionSettings.title),
    description: readString(data, 'heroDescription', defaultCorporateHeroSectionSettings.description),
    backgroundColor: readString(
      data,
      'heroBackgroundColor',
      defaultCorporateHeroSectionSettings.backgroundColor
    ),
    height: readNumber(data, 'heroHeight', defaultCorporateHeroSectionSettings.height),
    enabled: resolveEnabled(data, parsedJson),
    templateType,
    settingsJson: parsedJson,
    design: mergeDesign(parsedJson.design),
    layout: mergeLayout(parsedJson.layout),
  };

  return {
    ...heroSection,
    ...(overrides ?? {}),
    design: {
      ...heroSection.design,
      ...(overrides?.design ?? {}),
    },
    layout: {
      ...heroSection.layout,
      ...(overrides?.layout ?? {}),
    },
  };
}

export function serializeCorporateHeroSettingsJson(
  settings: Pick<CorporateHeroSectionSettings, 'templateType' | 'enabled' | 'design' | 'layout'>
): string {
  const payload: CorporateHeroSettingsJson = {
    templateType: settings.templateType,
    enabled: settings.enabled !== false,
    design: settings.design,
    layout: settings.layout,
  };

  return JSON.stringify(payload);
}

export function buildCorporateHeroSavePayload(
  heroSection: CorporateHeroSectionSettings
): Record<string, unknown> {
  return {
    heroTitle: heroSection.title,
    heroDescription: heroSection.description,
    heroBackgroundColor: heroSection.backgroundColor,
    heroHeight: heroSection.height,
    heroTemplateType: heroSection.templateType,
    heroEnabled: heroSection.enabled !== false,
    heroSettingsJson: serializeCorporateHeroSettingsJson(heroSection),
  };
}
